import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  Email,
  Facebook,
  LinkedIn,
  LocalPhone,
  LocationOn,
  WaterDrop,
} from '@mui/icons-material';
import logo from './Assets/logo.png';

const FooterContainer = styled.footer`
  background:
    radial-gradient(circle at 85% 0%, rgba(14, 165, 233, 0.16), transparent 40%),
    linear-gradient(135deg, #06111f 0%, #0b1f3a 55%, #071428 100%);
  color: #cbd5e1;
  margin-top: auto;
  width: 100%;
`;

const Inner = styled.div`
  width: min(1180px, calc(100% - 36px));
  margin: 0 auto;
  padding: 64px 0 32px;

  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr 1.1fr;
  gap: clamp(28px, 5vw, 48px);

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    padding: 48px 0 28px;
  }
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  margin-bottom: 14px;
`;

const LogoImage = styled.img`
  width: 38px;
  height: 38px;
  object-fit: contain;
`;

const BrandName = styled.span`
  color: white;
  font-size: 1.15rem;
  font-weight: 800;
  letter-spacing: -0.4px;
`;

const BrandText = styled.p`
  margin: 0;
  max-width: 320px;
  font-size: 0.92rem;
  line-height: 1.7;
  color: #94a3b8;
`;

const SocialRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
`;

const SocialLink = styled.a`
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  color: #cbd5e1;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: #2563eb;
    color: white;
  }
`;

const ColumnTitle = styled.h4`
  margin: 0 0 18px;
  color: white;
  font-size: 0.86rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;

const LinkList = styled.ul`
  display: grid;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const FooterLink = styled(Link)`
  color: #94a3b8;
  text-decoration: none;
  font-size: 0.92rem;
  transition: color 0.2s ease;

  &:hover {
    color: #7dd3fc;
  }
`;

const ContactItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #94a3b8;
  font-size: 0.9rem;
  line-height: 1.5;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    font-size: 1.05rem;
    color: #38bdf8;
  }
`;

const BottomBar = styled.div`
  width: min(1180px, calc(100% - 36px));
  margin: 0 auto;
  padding: 22px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.08);

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;

  font-size: 0.84rem;
  color: #64748b;

  @media (max-width: 560px) {
    justify-content: center;
    text-align: center;
  }
`;

const BottomLinks = styled.div`
  display: flex;
  gap: 20px;

  a {
    color: #64748b;
    text-decoration: none;
  }

  a:hover {
    color: #94a3b8;
  }
`;

const Footer = () => {
  return (
    <FooterContainer>
      <Inner>
        <div>
          <Brand as={Link} to="/">
            <LogoImage src={logo} alt="AquaTools logo" />
            <BrandName>AQUA TOOLS</BrandName>
          </Brand>
          <BrandText>
            Smart NB-IoT water metering, analytics, and prepaid workflows for utilities,
            estates, and industrial sites.
          </BrandText>
          <SocialRow>
            <SocialLink href="https://www.linkedin.com/company/aquatools" target="_blank" rel="noreferrer" aria-label="AquaTools on LinkedIn">
              <LinkedIn fontSize="small" />
            </SocialLink>
            <SocialLink href="https://www.facebook.com/aquatools" target="_blank" rel="noreferrer" aria-label="AquaTools on Facebook">
              <Facebook fontSize="small" />
            </SocialLink>
          </SocialRow>
        </div>

        <div>
          <ColumnTitle>Company</ColumnTitle>
          <LinkList>
            <li><FooterLink to="/about">About</FooterLink></li>
            <li><FooterLink to="/products">Products</FooterLink></li>
            <li><FooterLink to="/portfolio">Projects</FooterLink></li>
            <li><FooterLink to="/blog">Blog</FooterLink></li>
            <li><FooterLink to="/contactus">Contact</FooterLink></li>
          </LinkList>
        </div>

        <div>
          <ColumnTitle>Platform</ColumnTitle>
          <LinkList>
            <li><FooterLink to="/login">Login</FooterLink></li>
            <li><FooterLink to="/dashboard">Dashboard</FooterLink></li>
          </LinkList>
        </div>

        <div>
          <ColumnTitle>Get in touch</ColumnTitle>
          <LinkList>
            <ContactItem>
              <Email />
              <span>info@aquatools.io</span>
            </ContactItem>
            <ContactItem>
              <LocalPhone />
              <span>+254 713 522 833</span>
            </ContactItem>
            <ContactItem>
              <LocationOn />
              <span>Nairobi, Kenya</span>
            </ContactItem>
          </LinkList>
        </div>
      </Inner>

      <BottomBar>
        <span>&copy; {new Date().getFullYear()} AquaTools. All rights reserved.</span>
        <BottomLinks>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <WaterDrop style={{ fontSize: 14 }} /> Built for smarter water management
          </span>
        </BottomLinks>
      </BottomBar>
    </FooterContainer>
  );
};

export default Footer;
